import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import LoginPage from "./Home/LoginPage";
import NotFound from "./NotFound";
import { AuthProvider, useAuth } from "./AuthContext";
import MainLayout from "./Components/Layout/MainLayout";

// Operational pages (kept for routing)
import SettlementsPending from "./Settlements/SettlementsPending";
import SettlementsReady from "./Settlements/SettlementsReady";
import SettlementsSettled from "./Settlements/SettlementsSettled";
import SettlementsFailed from "./Settlements/SettlementsFailed";

// Analytics tab components
import AnalyticsPageWrapper from "./PlatformAnalytics/AnalyticsPageWrapper";
import OverviewTab from "./PlatformAnalytics/tabs/OverviewTab";
import GMVTab from "./PlatformAnalytics/tabs/GMVTab";
import RevenueTab from "./PlatformAnalytics/tabs/RevenueTab";
import FunnelTab from "./PlatformAnalytics/tabs/FunnelTab";
import BrandPagesTab from "./PlatformAnalytics/tabs/TopCreatorsTab";
import SettlementsAnalyticsTab from "./PlatformAnalytics/tabs/SettlementsAnalyticsTab";
import CustomersTab from "./PlatformAnalytics/tabs/CustomersTab";
import TransactionsTab from "./PlatformAnalytics/tabs/TransactionsTab";
import UsersTab from "./PlatformAnalytics/tabs/UsersTab";
import UpdateInstagramDetailsTab from "./PlatformAnalytics/tabs/UpdateInstagramDetailsTab";
import LeadsTab from "./PlatformAnalytics/tabs/LeadsTab";
import NewRegistrationsTab from "./PlatformAnalytics/tabs/NewRegistrationsTab";
import AdminUsersPage from "./PlatformAnalytics/tabs/AdminUsersPage";
import SEOBlogsPage from "./PlatformAnalytics/tabs/SEOBlogsPage";
import SaasPlansTab from "./PlatformAnalytics/tabs/SaasPlansTab";
import InstaPlansTab from "./PlatformAnalytics/tabs/InstaPlansTab";

// Legacy pages
import Features from "./Features/Features";
import Metrics from "./Components/Metrics";
import HelpAndSupport from "./HelpAndSupport/HelpAndSupport";
import Broadcast from "./Broadcast/Broadcast";
import OnboardingMetrics from "./OnboardingMetrics/OnboardingMetrics";
import Subdomains from "./Components/Subdomains";

// Category Pages (super-admin)
import CategoryPagesList from "./CategoryPages/CategoryPagesList";
import CategoryPageForm from "./CategoryPages/CategoryPageForm";
import CategoryPageDetail from "./CategoryPages/CategoryPageDetail";

// Photo Cloud
import PhotoCloudPage from "./PhotoCloud/PhotoCloudPage";
// Nurture (WhatsApp nurture sequence — Prospect → Onboarded funnel)
import NurtureTemplates from "./Nurture/NurtureTemplates";
import NurtureMessages from "./Nurture/NurtureMessages";
import NurtureAnalytics from "./Nurture/NurtureAnalytics";
import NurtureCategoryImages from "./Nurture/NurtureCategoryImages";

// CRM v2 (multi-stage funnel)
import CrmDashboard from "./Crm/CrmDashboard";
import PreOnboardBoard from "./Crm/PreOnboardBoard";
import PostOnboardBoard from "./Crm/PostOnboardBoard";

// Founder Goals
import GoalsPage from "./Goals/GoalsPage";

// Renewal Reminders (plan-expiry WhatsApp messages)
import RenewalRemindersPage from "./RenewalReminders/RenewalRemindersPage";

const ANALYTICS_PAGES = [
  {
    path: "overview",
    title: "Overview",
    subtitle: "Platform-wide KPIs across signups, revenue, and more.",
    Component: OverviewTab,
  },
  {
    path: "gmv",
    title: "GMV",
    subtitle: "Gross Merchandise Value tracked monthly.",
    Component: GMVTab,
  },
  {
    path: "revenue",
    title: "Revenue",
    subtitle: "Platform fee earned over time.",
    Component: RevenueTab,
  },
  {
    path: "funnel",
    title: "Creator Funnel",
    subtitle: "Drop-off analysis across the creator lifecycle.",
    Component: FunnelTab,
  },
  {
    path: "brand-pages",
    title: "Brand Pages",
    subtitle: "All brand pages sorted by GMV. Click a row for full details.",
    Component: BrandPagesTab,
  },
  {
    path: "users",
    title: "Users",
    subtitle: "All platform users with onboarding status and brand pages.",
    Component: UsersTab,
  },
  {
    path: "settlements",
    title: "Settlement Stats",
    subtitle: "Settlement status breakdown, timing and payout summary.",
    Component: SettlementsAnalyticsTab,
  },
  {
    path: "customers",
    title: "Customers",
    subtitle: "Booking behaviour, repeat rates and top spenders.",
    Component: CustomersTab,
  },
  {
    path: "transactions",
    title: "Transactions",
    subtitle: "Full transaction ledger with search and filters.",
    Component: TransactionsTab,
  },
  {
    path: "leads",
    title: "All Contacts",
    subtitle:
      "Full CRM contact list — every person at every stage. Search, filter, export.",
    Component: LeadsTab,
  },
  {
    path: "new-registrations",
    title: "New Registrations",
    subtitle: "Users and brand pages created in the selected date range.",
    Component: NewRegistrationsTab,
  },
  {
    path: "saas-plans",
    title: "SaaS Plans",
    subtitle:
      "Free / Growth / Pro / Advanced plan adoption. Click a card to see who is on it.",
    Component: SaasPlansTab,
  },
  {
    path: "insta-plans",
    title: "Instagram Automation Plans",
    subtitle:
      "Adoption of Instagram automation plans (free vs. paid packs). Click a card to drill down.",
    Component: InstaPlansTab,
  },
  {
    path: "seo-blogs",
    title: "SEO Blogs",
    subtitle:
      "Manage SEO pillars, knowledge base, and AI-generated blog content.",
    Component: SEOBlogsPage,
    noDateRange: true,
  },
  {
    path: "update-instagram-details",
    title: "Update Instagram Details",
    subtitle:
      "Manually enrich Instagram profile URL, follower count, and event host type for signed-up users.",
    Component: UpdateInstagramDetailsTab,
    noDateRange: true,
  },
];

function App() {
  return (
    <AuthProvider>
      <SnackbarProvider
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        maxSnack={3}
        autoHideDuration={3000}
        hideIconVariant={true}
      >
        <BrowserRouter>
          <MainRoutes />
        </BrowserRouter>
      </SnackbarProvider>
    </AuthProvider>
  );
}

const MainRoutes = () => {
  const { isLoggedIn } = useAuth();

  const ProtectedRoute = ({ children }) => {
    return isLoggedIn ? children : <Navigate to="/" />;
  };

  return (
    <Routes>
      {/* Public Route */}
      <Route
        path="/"
        element={
          isLoggedIn ? <Navigate to="/analytics/overview" /> : <LoginPage />
        }
      />

      {/* Protected Routes with Sidebar Layout */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Analytics pages */}
        {ANALYTICS_PAGES.map(
          ({ path, title, subtitle, Component, noDateRange }) => (
            <Route
              key={path}
              path={`/analytics/${path}`}
              element={
                <AnalyticsPageWrapper
                  title={title}
                  subtitle={subtitle}
                  Component={Component}
                  noDateRange={noDateRange}
                />
              }
            />
          ),
        )}

        {/* Redirect bare /analytics to overview */}
        <Route
          path="/analytics"
          element={<Navigate to="/analytics/overview" />}
        />

        {/* Redirect old routes */}
        <Route
          path="/analytics/growth"
          element={<Navigate to="/analytics/gmv" />}
        />
        <Route
          path="/analytics/marketing"
          element={<Navigate to="/analytics/overview" />}
        />
        <Route
          path="/analytics/top-creators"
          element={<Navigate to="/analytics/brand-pages" />}
        />

        {/* Operational Settlements (Payouts) — untouched */}
        <Route path="/settlements/pending" element={<SettlementsPending />} />
        <Route path="/settlements/ready" element={<SettlementsReady />} />
        <Route path="/settlements/failed" element={<SettlementsFailed />} />
        <Route path="/settlements/settled" element={<SettlementsSettled />} />

        {/* Admin Users (superadmin only) */}
        <Route path="/admin-users" element={<AdminUsersPage />} />

        {/* Category Pages (superadmin only) */}
        <Route path="/category-pages" element={<CategoryPagesList />} />
        <Route
          path="/category-pages/new"
          element={<CategoryPageForm mode="create" />}
        />
        <Route path="/category-pages/:id" element={<CategoryPageDetail />} />
        <Route
          path="/category-pages/:id/edit"
          element={<CategoryPageForm mode="edit" />}
        />

        {/* Photo Cloud */}
        <Route path="/photo-cloud" element={<PhotoCloudPage />} />
        {/* Nurture (WhatsApp prospect funnel) */}
        <Route path="/nurture" element={<Navigate to="/nurture/templates" />} />
        <Route path="/nurture/templates" element={<NurtureTemplates />} />
        <Route
          path="/nurture/category-images"
          element={<NurtureCategoryImages />}
        />
        <Route path="/nurture/messages" element={<NurtureMessages />} />
        <Route path="/nurture/analytics" element={<NurtureAnalytics />} />

        {/* CRM v2 (multi-stage funnel) */}
        <Route path="/crm" element={<Navigate to="/crm/dashboard" />} />
        <Route path="/crm/dashboard" element={<CrmDashboard />} />
        <Route path="/crm/pre-onboard" element={<PreOnboardBoard />} />
        <Route path="/crm/post-onboard" element={<PostOnboardBoard />} />

        {/* Founder Goals */}
        <Route path="/goals" element={<GoalsPage />} />

        {/* Renewal Reminders */}
        <Route path="/renewal-reminders" element={<RenewalRemindersPage />} />

        {/* Legacy routes */}
        <Route path="/home" element={<Navigate to="/analytics/overview" />} />
        <Route
          path="/dashboard"
          element={<Navigate to="/analytics/overview" />}
        />
        <Route path="/client" element={<Navigate to="/analytics/overview" />} />
        <Route
          path="/insta-clients"
          element={<Navigate to="/analytics/overview" />}
        />
        <Route path="/features" element={<Features />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/onboarding_metrics" element={<OnboardingMetrics />} />
        <Route path="/helpAndSupport" element={<HelpAndSupport />} />
        <Route path="/broadcast" element={<Broadcast />} />
        <Route path="/subdomainsClients" element={<Subdomains />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
